export const PIXELS_PER_FOOT = 20;

export function ftToPx(ft: number): number {
  return ft * PIXELS_PER_FOOT;
}

export function pxToFt(px: number): number {
  return px / PIXELS_PER_FOOT;
}

export function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

export const CHAIR_SIZE_FT = 1.5;

export function calcCeremonyPositions(
  preset: 'grid' | 'curved' | 'vshape',
  rows: number,
  chairsPerRow: number,
  chairSpacingFt: number,
  rowSpacingFt: number,
  withAisle: boolean,
  aisleWidthFt: number,
): { positions: { x: number; y: number }[]; widthFt: number; heightFt: number } {
  const cs = CHAIR_SIZE_FT;
  const cstep = cs + chairSpacingFt;
  const rstep = cs + rowSpacingFt;
  const positions: { x: number; y: number }[] = [];

  if (preset === 'curved') {
    const spreadAngle = Math.PI * 0.75;
    for (let r = 0; r < rows; r++) {
      const radius = (chairsPerRow * cstep) / spreadAngle + r * rstep;
      const cx = (chairsPerRow * cstep) / 2;
      const cy = radius;
      for (let c = 0; c < chairsPerRow; c++) {
        const a = Math.PI - spreadAngle / 2 + (spreadAngle / Math.max(chairsPerRow - 1, 1)) * c;
        positions.push({ x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) });
      }
    }
    const minX = Math.min(...positions.map((p) => p.x));
    const minY = Math.min(...positions.map((p) => p.y));
    const norm = positions.map((p) => ({ x: p.x - minX, y: p.y - minY }));
    const maxX = Math.max(...norm.map((p) => p.x));
    const maxY = Math.max(...norm.map((p) => p.y));
    return { positions: norm, widthFt: maxX + cs, heightFt: maxY + cs };
  }

  if (preset === 'vshape') {
    const half = Math.floor(chairsPerRow / 2);
    const other = chairsPerRow - half;
    const gap = withAisle ? aisleWidthFt : cstep;
    for (let r = 0; r < rows; r++) {
      const inset = r * 1.0;
      for (let c = 0; c < half; c++) positions.push({ x: inset + c * cstep, y: r * rstep });
      const rightStart = half * cstep + gap;
      for (let c = 0; c < other; c++)
        positions.push({ x: rightStart + c * cstep - inset, y: r * rstep });
    }
  } else {
    // grid
    const half = Math.floor(chairsPerRow / 2);
    const other = chairsPerRow - half;
    for (let r = 0; r < rows; r++) {
      if (withAisle) {
        for (let c = 0; c < half; c++) positions.push({ x: c * cstep, y: r * rstep });
        const rightStart = half * cstep + aisleWidthFt;
        for (let c = 0; c < other; c++) positions.push({ x: rightStart + c * cstep, y: r * rstep });
      } else {
        for (let c = 0; c < chairsPerRow; c++) positions.push({ x: c * cstep, y: r * rstep });
      }
    }
  }

  const maxX = positions.length > 0 ? Math.max(...positions.map((p) => p.x)) : 0;
  const maxY = positions.length > 0 ? Math.max(...positions.map((p) => p.y)) : 0;
  return { positions, widthFt: maxX + cs, heightFt: maxY + cs };
}

export function getTableColor(
  seated: number,
  capacity: number,
): { fill: string; stroke: string; label: string } {
  if (seated === 0) return { fill: '#f3f4f6', stroke: '#9ca3af', label: 'Empty' };
  if (seated >= capacity) return { fill: '#dcfce7', stroke: '#16a34a', label: 'Full' };
  return { fill: '#fef9c3', stroke: '#ca8a04', label: 'Partial' };
}

// ─── Conversion: Backend FloorPlanObject ↔ CanvasItem ──────────
//
// Tables and non-table objects all live in `floor_plan_objects` now. An
// object is treated as a seatable table when its `tableNumber` is set.

import {
  calculateRoundTableChairs,
  calculateRectTableChairs,
  type BulkUpsertObjectsInput,
  type ChairSides,
  type FloorPlanObject,
} from '@peles/shared';
import type { CanvasItem } from './types';
import { TABLE_TYPE_REVERSE, isTableItem } from './types';

export function fromFloorPlanObject(o: FloorPlanObject): CanvasItem {
  const config = (o.config as Record<string, unknown>) ?? {};
  // Prefer the top-level column; fall back to a legacy config.tableNumber for
  // rows that pre-date the unification migration and haven't been backfilled
  // yet (e.g. local dev DBs that haven't run the latest migration).
  const tableNumber =
    typeof o.tableNumber === 'number'
      ? o.tableNumber
      : typeof config.tableNumber === 'number'
        ? (config.tableNumber as number)
        : undefined;
  // Strip tableNumber out of the canvas-side config so we never round-trip
  // it back to the JSON column on save.
  const cleanConfig: Record<string, unknown> = { ...config };
  delete cleanConfig.tableNumber;
  return {
    id: o.id,
    objectType: o.objectType,
    label: o.label || '',
    x: o.x,
    y: o.y,
    widthFt: o.widthFt,
    heightFt: o.heightFt,
    rotation: o.rotation,
    color: o.color,
    opacity: o.opacity,
    seats: o.seats ?? 0,
    config: cleanConfig,
    sortOrder: o.sortOrder,
    ...(typeof tableNumber === 'number' ? { tableNumber } : {}),
    // groupId is still persisted inside config.
    ...(typeof cleanConfig.groupId === 'string' ? { groupId: cleanConfig.groupId } : {}),
  };
}

/**
 * Convert canvas items to the bulk-upsert payload. Folds the UI-only
 * `groupId` into `config` so it round-trips through the JSON column.
 * Pass `stripIds` when saving into a different floor plan (e.g. Save As) —
 * the DB ids belong to the source plan's objects.
 */
export function itemsToObjectsPayload(
  items: CanvasItem[],
  opts?: { stripIds?: boolean },
): BulkUpsertObjectsInput['objects'] {
  return items.map((it) => {
    const itemToSave = it.groupId ? { ...it, config: { ...it.config, groupId: it.groupId } } : it;
    const o = toFloorPlanObject(itemToSave);
    return {
      ...o,
      id: opts?.stripIds ? undefined : o.id,
      label: o.label ?? undefined,
      seats: o.seats ?? undefined,
      tableNumber: o.tableNumber ?? undefined,
      config: o.config ?? undefined,
    };
  });
}

// ─── Chair attachment ───────────────────────────────────────────
//
// Placed chairs link to their parent via config.parentTableNumber (tables)
// or config.parentObjectSort (chair-capable objects). Every operation that
// moves, deletes, or re-seats a parent must carry its chairs along, so the
// linkage logic lives here rather than being re-derived at each call site.

export const CHAIR_COLOR = '#9ca3af';

export function chairParentRef(parent: CanvasItem): {
  field: 'parentTableNumber' | 'parentObjectSort';
  key: number;
} {
  return isTableItem(parent)
    ? { field: 'parentTableNumber', key: parent.tableNumber! }
    : { field: 'parentObjectSort', key: parent.sortOrder };
}

export function isChairOf(chair: CanvasItem, parent: CanvasItem): boolean {
  if (chair.objectType !== 'CHAIR' || parent.objectType === 'CHAIR') return false;
  const { field, key } = chairParentRef(parent);
  return chair.config?.[field] === key;
}

export function canParentChairs(item: CanvasItem): boolean {
  return item.objectType !== 'CHAIR' && (isTableItem(item) || !!item.config?.canHaveChairs);
}

/** Indexes of all chairs attached to any of the given parents. */
export function attachedChairIndexes(items: CanvasItem[], parents: CanvasItem[]): Set<number> {
  const result = new Set<number>();
  const nonChairParents = parents.filter((p) => p && p.objectType !== 'CHAIR');
  if (nonChairParents.length === 0) return result;
  items.forEach((it, i) => {
    if (nonChairParents.some((p) => isChairOf(it, p))) result.add(i);
  });
  return result;
}

/** Whether chairs should be laid out radially (round/oval outline). */
export function isRoundParent(item: CanvasItem): boolean {
  const tableType = isTableItem(item)
    ? TABLE_TYPE_REVERSE[item.objectType] || item.objectType.replace('TABLE_', '')
    : null;
  const shape = (item.config?.shape as string) || '';
  return tableType === 'ROUND' || tableType === 'OVAL' || shape === 'CIRCLE' || shape === 'OVAL';
}

/**
 * Build the CHAIR items for a parent at its current position/rotation.
 * Chair centers are rotated around the table center so chairs track the
 * parent's rotation.
 */
export function buildChairsForParent(
  parent: CanvasItem,
  opts: {
    chairSizeFt: number;
    chairSides: ChairSides;
    sortBase: number;
    groupId: string;
    seats?: number;
  },
): CanvasItem[] {
  const seats = opts.seats ?? parent.seats;
  const { field, key } = chairParentRef(parent);
  const round = isRoundParent(parent);
  const positions = round
    ? calculateRoundTableChairs(parent.widthFt, seats, opts.chairSizeFt)
    : calculateRectTableChairs(
        parent.widthFt,
        parent.heightFt,
        seats,
        opts.chairSizeFt,
        opts.chairSides,
      );

  const centerX = parent.x + ftToPx(parent.widthFt) / 2;
  const centerY = parent.y + ftToPx(parent.heightFt) / 2;
  const rotRad = (parent.rotation * Math.PI) / 180;
  const cSzPx = ftToPx(opts.chairSizeFt);
  const halfTable = { x: ftToPx(parent.widthFt) / 2, y: ftToPx(parent.heightFt) / 2 };

  return positions.map((pos, i) => {
    const chairCenterX = round ? ftToPx(pos.x) : ftToPx(pos.x) + cSzPx / 2 - halfTable.x;
    const chairCenterY = round ? ftToPx(pos.y) : ftToPx(pos.y) + cSzPx / 2 - halfTable.y;
    const rx = chairCenterX * Math.cos(rotRad) - chairCenterY * Math.sin(rotRad);
    const ry = chairCenterX * Math.sin(rotRad) + chairCenterY * Math.cos(rotRad);
    return {
      objectType: 'CHAIR',
      label: `S${i + 1}`,
      x: centerX + rx - cSzPx / 2,
      y: centerY + ry - cSzPx / 2,
      widthFt: opts.chairSizeFt,
      heightFt: opts.chairSizeFt,
      rotation: Math.round(pos.rotation + parent.rotation) % 360,
      color: CHAIR_COLOR,
      opacity: 1,
      seats: 1,
      config: { [field]: key },
      sortOrder: opts.sortBase + i,
      groupId: opts.groupId,
    };
  });
}

/**
 * Remove and rebuild the chairs of the parents at the given indexes, using
 * each parent's CURRENT seats/size/position. Parents that have no chairs are
 * skipped unless `placeIfMissing`. Chair size defaults to the parent's
 * existing chair size so a seats-only change keeps the configuration.
 */
export function replaceChairsForParents(
  items: CanvasItem[],
  parentIdxs: number[],
  opts: {
    chairSides: ChairSides;
    defaultChairSizeFt: number;
    chairSizeFt?: number;
    placeIfMissing?: boolean;
  },
): CanvasItem[] {
  const parents = parentIdxs
    .map((i) => items[i])
    .filter((p): p is CanvasItem => !!p && canParentChairs(p));
  if (parents.length === 0) return items;

  const plans: { parent: CanvasItem; chairSizeFt: number; groupId: string }[] = [];
  const chairsToRemove = new Set<CanvasItem>();
  parents.forEach((parent) => {
    const existing = items.filter((it) => isChairOf(it, parent));
    if (existing.length === 0 && !opts.placeIfMissing) return;
    existing.forEach((c) => chairsToRemove.add(c));
    const { field, key } = chairParentRef(parent);
    plans.push({
      parent,
      chairSizeFt: opts.chairSizeFt ?? existing[0]?.widthFt ?? opts.defaultChairSizeFt,
      groupId: parent.groupId || `chairs-${field}-${key}-${Date.now()}`,
    });
  });
  if (plans.length === 0) return items;

  let result = items
    .filter((it) => !chairsToRemove.has(it))
    .map((it) => {
      const plan = plans.find((p) => p.parent === it);
      if (!plan) return it;
      const updated = it.groupId !== plan.groupId ? { ...it, groupId: plan.groupId } : it;
      plan.parent = updated;
      return updated;
    });
  plans.forEach(({ parent, chairSizeFt, groupId }) => {
    result = [
      ...result,
      ...buildChairsForParent(parent, {
        chairSizeFt,
        chairSides: opts.chairSides,
        sortBase: result.length,
        groupId,
      }),
    ];
  });
  return result;
}

/**
 * Apply per-item position deltas, extending each delta to the item's
 * attached chairs (unless the chair has its own delta already). Positions
 * are clamped at 0.
 */
export function moveItemsWithChairs(
  items: CanvasItem[],
  deltas: Map<number, { dx: number; dy: number }>,
): CanvasItem[] {
  const extended = new Map(deltas);
  deltas.forEach((d, idx) => {
    const parent = items[idx];
    if (!parent || parent.objectType === 'CHAIR') return;
    items.forEach((it, i) => {
      if (!extended.has(i) && isChairOf(it, parent)) extended.set(i, d);
    });
  });
  return items.map((it, i) => {
    const d = extended.get(i);
    if (!d || (d.dx === 0 && d.dy === 0)) return it;
    return { ...it, x: Math.max(0, it.x + d.dx), y: Math.max(0, it.y + d.dy) };
  });
}

/**
 * Expand a seed selection to everything that moves with it: whole groups of
 * any member (transitively) and the attached chairs of any non-chair member.
 */
export function expandMoveSet(items: CanvasItem[], seed: Iterable<number>): Set<number> {
  const set = new Set(seed);
  // New entries added during forEach are visited too, so group expansion is
  // transitive.
  set.forEach((mi) => {
    const item = items[mi];
    if (item?.groupId) {
      items.forEach((it, i) => {
        if (it.groupId === item.groupId) set.add(i);
      });
    }
  });
  const parents = [...set].map((i) => items[i]).filter((it): it is CanvasItem => !!it);
  attachedChairIndexes(items, parents).forEach((i) => set.add(i));
  return set;
}

/** Re-point chairs at a parent whose table number is about to change. */
export function retargetChairs(
  items: CanvasItem[],
  parent: CanvasItem,
  newKey: number,
): CanvasItem[] {
  const { field } = chairParentRef(parent);
  return items.map((it) =>
    isChairOf(it, parent) ? { ...it, config: { ...it.config, [field]: newKey } } : it,
  );
}

export function toFloorPlanObject(
  item: CanvasItem,
): Omit<FloorPlanObject, 'id' | 'floorPlanId' | 'createdAt' | 'updatedAt'> & { id?: string } {
  return {
    id: item.id,
    objectType: item.objectType as FloorPlanObject['objectType'],
    label: item.label || null,
    x: item.x,
    y: item.y,
    widthFt: item.widthFt,
    heightFt: item.heightFt,
    rotation: item.rotation,
    color: item.color,
    opacity: item.opacity,
    seats: item.seats ?? null,
    tableNumber: typeof item.tableNumber === 'number' ? item.tableNumber : null,
    config: Object.keys(item.config).length > 0 ? item.config : null,
    sortOrder: item.sortOrder,
  };
}
